/**
 * @lume/shared — **canal DURÁVEL × canal de SESSÃO**.
 *
 * ── A pergunta que este módulo responde ───────────────────────────────────────────────
 * «Este contato PERTENCE à pessoa e sobrevive, ou é ONDE ELA ESTÁ SENTADA agora?»
 *
 * Um número de WhatsApp, um e-mail, um id de Telegram são ENDEREÇOS — dá para mandar algo
 * amanhã. Uma sessão de navegador é uma CADEIRA — some quando a aba fecha.
 *
 * ── Por que não reusar constante existente ────────────────────────────────────────────
 * `CHANNELS_WITH_REAL_ADAPTER` responde «tem adapter para ENVIAR?» — pergunta diferente, e
 * usá-la como filtro de destino tem defeito de tempo: no dia em que o canal webchat de
 * produção for materializado, ele entra naquela constante e a sessão viraria destino
 * legítimo **exatamente quando o canal ficasse pronto**. `CHANNEL_FAMILY_BY_KIND` responde
 * «qual a NORMA de conversa deste canal?» — também outra pergunta.
 *
 * Três perguntas, três respostas separadas. Fundir duas é o defeito que a frente do chat do
 * lado cliente mediu três vezes (spec `2026-08-18-webchat-do-cliente-tres-cenarios`, §0-bis).
 *
 * ── Polaridade ────────────────────────────────────────────────────────────────────────
 * `isDurableChannel` é **fail-safe**: desconhecido ⇒ NÃO durável. Diferente de
 * `channelToFamily`, que devolve `null` — ali a família é uma afirmação sobre a norma da
 * conversa e não se afirma nada sobre canal desconhecido. Aqui a resposta alimenta decisão
 * de SEGURANÇA (para onde vai a confirmação de titularidade), e o desconhecido cai no
 * conservador.
 *
 * ── Camada ────────────────────────────────────────────────────────────────────────────
 * **Camada 1, plataforma.** Durabilidade é FATO técnico do canal, não preferência do
 * tenant. Nenhum literal de setor, produto ou mercado.
 */
export const CHANNEL_DURABILITIES = [
    /** O contato pertence à pessoa e sobrevive à conversa. Endereçável amanhã. */
    'durable',
    /** Onde a pessoa está agora. Deixa de existir quando a sessão termina. */
    'session',
];
/**
 * Exaustivo por construção (`Record<ChannelKind, …>`): canal novo sem durabilidade é erro
 * de COMPILAÇÃO, não um `else` silencioso. Mesmo molde de `CHANNEL_FAMILY_BY_KIND`.
 */
export const CHANNEL_DURABILITY_BY_KIND = {
    'whatsapp-cloud-api': 'durable',
    telegram: 'durable',
    'instagram-dm': 'durable',
    email: 'durable',
    webchat: 'session',
    // Conversa simulada não é contato que pertence a ninguém.
    simulador: 'session',
};
/**
 * Normaliza para a forma de APP (hífen). O banco grava a forma do enum Prisma
 * (underscore), e este módulo é consumido dos dois lados — então aceita as duas em vez de
 * exigir que cada caller lembre de converter.
 */
function normalize(raw) {
    return raw.trim().toLowerCase().replaceAll('_', '-');
}
const DURABILITY_BY_RAW = new Map(Object.entries(CHANNEL_DURABILITY_BY_KIND));
/**
 * O contato deste canal é durável? **Fail-safe: desconhecido ⇒ `false`.** Nunca lança.
 *
 * Consumidor canônico: a escolha do destino da confirmação de titularidade
 * (`CandidateResolver`). Um `true` indevido aqui manda a confirmação de uma mudança
 * cadastral para uma janela que o próprio solicitante controla.
 */
export function isDurableChannel(raw) {
    if (typeof raw !== 'string')
        return false;
    const key = normalize(raw);
    if (key.length === 0)
        return false;
    return DURABILITY_BY_RAW.get(key) === 'durable';
}
//# sourceMappingURL=channel-durability.js.map